import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CashierAnalytics.css';
import { getCashierAnalyticsMock, getCashierTransactionsMock } from '../../api/cashierApi';
import CardComponent from '../../components/cards/CardComponent';
import FloatingNavModal from '../../components/floatingbuttons/FloatingNavModal';
import { 
  FaDollarSign, 
  FaCreditCard, 
  FaUniversity, 
  FaExchangeAlt,
  FaTrophy,
  FaClock,
  FaHome,
  FaChartBar,
  FaFileAlt,
  FaCalendarAlt,
  FaUser,
  FaSpinner,
  FaExclamationTriangle
} from 'react-icons/fa';
import { ToastContainer } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const CashierAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  // Get user information from localStorage/sessionStorage
  const userType = (localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '').toLowerCase();
  const userId = localStorage.getItem('userid') || sessionStorage.getItem('userid') || localStorage.getItem('uname') || sessionStorage.getItem('uname');
  const userName = localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'Cashier';
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  // Check if user is authenticated and is a cashier
  const isCashier = userType === 'cashier';
  const isAuthenticated = !!token;

  const fetchCashierData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Using mock data for now - replace with actual API calls later
      const [analytics, transactionList] = await Promise.all([
        getCashierAnalyticsMock(userId, selectedPeriod),
        getCashierTransactionsMock(userId, selectedPeriod)
      ]);

      setAnalyticsData(analytics);
      setTransactions(transactionList);
    } catch (err) {
      console.error('Error fetching cashier data:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId, selectedPeriod]);

  useEffect(() => {
    // Check authentication first
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Check if user is cashier
    if (!isCashier) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }

    // If authenticated and is cashier, load data
    if (userId) {
      fetchCashierData();
    }
  }, [isAuthenticated, isCashier, userId, navigate, fetchCashierData]);

  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  if (accessDenied) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading"><FaExclamationTriangle /> Access Denied</h4>
          <p>This analytics page is only available for cashier accounts.</p>
          <p>Your current role: <strong>{userType || 'Unknown'}</strong></p>
          <p>Please contact your administrator if you believe this is an error.</p>
          <hr />
          <div className="d-flex gap-2">
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/dashboard/admin')}
            >
              <FaHome /> Go to Dashboard
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => {
                localStorage.clear();
                sessionStorage.clear();
                navigate('/login');
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="d-flex flex-column align-items-center">
          <FaSpinner className="fa-spin mb-3" size={48} color="#007bff" />
          <h5>Loading analytics data...</h5>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <p>{error}</p>
          <button 
            className="btn btn-primary"
            onClick={fetchCashierData}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cashier-analytics">
      <ToastContainer />
      
      {/* Header Section */}
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col-lg-9">
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
              <div className="d-flex align-items-center">
                <button 
                  className="btn btn-outline-primary me-3 back-to-dashboard"
                  onClick={() => navigate('/dashboard/cashier')}
                >
                  <FaHome /> Back to Dashboard
                </button>
                <h2 className="mb-0">
                  <FaChartBar /> Cashier Analytics
                </h2>
              </div>
            </div>
            <p className="text-muted">Detailed analytics and reports for your transactions and performance</p>
          </div>
          <div className="col-lg-3">
            <div className="info-sidebar">
              <CardComponent title="Current Session" headerColor="info" pull="left">
                <div className="session-info">
                  <div className="info-item">
                    <FaCalendarAlt className="text-info me-2" />
                    <div>
                      <strong>Billing Date</strong>
                      <div className="text-muted">{new Date().toLocaleDateString('en-IN', { 
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}</div>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaUser className="text-info me-2" />
                    <div>
                      <strong>Cashier</strong>
                      <div className="text-muted">{userName}</div>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaClock className="text-info me-2" />
                    <div>
                      <strong>Login Time</strong>
                      <div className="text-muted">{new Date().toLocaleTimeString()}</div>
                    </div>
                  </div>
                </div>
              </CardComponent>
              
              <CardComponent title="Quick Stats" headerColor="secondary" pull="left">
                <div className="quick-stats">
                  <div className="stat-row">
                    <span className="stat-label">Active Tables:</span>
                    <span className="stat-value text-primary">8</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Pending Orders:</span>
                    <span className="stat-value text-warning">3</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Today's Goal:</span>
                    <span className="stat-value text-success">₹30,000</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Shift Status:</span>
                    <span className="badge bg-success">Active</span>
                  </div>
                </div>
              </CardComponent>

              <CardComponent title="Notifications" headerColor="warning" pull="left">
                <div className="notifications">
                  <div className="notification-item">
                    <FaExclamationTriangle className="text-warning me-2" />
                    <small>Table 5 payment pending</small>
                  </div>
                  <div className="notification-item">
                    <FaClock className="text-info me-2" />
                    <small>Break time in 1 hour</small>
                  </div>
                </div>
              </CardComponent>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="row mb-4">
          <div className="col-lg-9">
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">Time Period:</label>
                <select 
                  className="form-select" 
                  value={selectedPeriod} 
                  onChange={handlePeriodChange}
                >
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">Start Date:</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  className="form-control"
                  disabled={selectedPeriod !== 'custom'}
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label fw-bold">End Date:</label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  className="form-control"
                  disabled={selectedPeriod !== 'custom'}
                />
              </div>
            </div>
          </div>
        </div>

        {analyticsData && (
          <>
            {/* Key Metrics */}
            <div className="row mb-4">
              <div className="col-lg-9">
                <div className="row">
                  <div className="col-lg-3 col-md-6 mb-3">
                    <CardComponent
                      title="Total Revenue"
                      headerColor="success"
                      pull="left"
                    >
                      <div className="metric-content">
                        <div className="metric-value text-success">
                          ₹{analyticsData.totalRevenue.toLocaleString()}
                        </div>
                        <div className={`metric-change ${analyticsData.revenueChange >= 0 ? 'text-success' : 'text-danger'}`}>
                          {analyticsData.revenueChange > 0 ? '+' : ''}{analyticsData.revenueChange}% from last period
                        </div>
                      </div>
                    </CardComponent>
                  </div>
                  
                  <div className="col-lg-3 col-md-6 mb-3">
                    <CardComponent
                      title="Total Transactions"
                      headerColor="primary"
                      pull="left"
                    >
                      <div className="metric-content">
                        <div className="metric-value text-primary">
                          <FaExchangeAlt /> {analyticsData.totalTransactions}
                        </div>
                        <div className="text-muted">Transactions processed</div>
                      </div>
                    </CardComponent>
                  </div>

                  <div className="col-lg-3 col-md-6 mb-3">
                    <CardComponent
                      title="Average Order"
                      headerColor="info"
                      pull="left"
                    >
                      <div className="metric-content">
                        <div className="metric-value text-info">
                          ₹{analyticsData.avgOrderValue.toFixed(2)}
                        </div>
                        <div className="text-muted">Per transaction</div>
                      </div>
                    </CardComponent>
                  </div>

                  <div className="col-lg-3 col-md-6 mb-3">
                    <CardComponent
                      title="Collection Rate"
                      headerColor={analyticsData.collectionRate >= 95 ? 'success' : 'warning'}
                      pull="left"
                    >
                      <div className="metric-content">
                        <div className={`metric-value ${analyticsData.collectionRate >= 95 ? 'text-success' : 'text-warning'}`}>
                          {analyticsData.collectionRate}%
                        </div>
                        <div className="progress" style={{height: '6px'}}>
                          <div 
                            className={`progress-bar ${analyticsData.collectionRate >= 95 ? 'bg-success' : 'bg-warning'}`}
                            style={{width: `${analyticsData.collectionRate}%`}}
                          ></div>
                        </div>
                      </div>
                    </CardComponent>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Breakdown */}
            <div className="row mb-4">
              <div className="col-lg-4 mb-3">
                <CardComponent
                  title="Cash Payments"
                  headerColor="success"
                  pull="left"
                >
                  <div className="payment-breakdown">
                    <div className="payment-amount">
                      <FaDollarSign className="text-success" />
                      <span className="ms-2 fw-bold">₹{analyticsData.cashAmount.toLocaleString()}</span>
                    </div>
                    <div className="payment-details mt-2">
                      <small className="text-muted">{analyticsData.cashCount} transactions</small>
                      <span className="badge bg-success">{analyticsData.cashPercentage}% of total</span>
                    </div>
                  </div>
                </CardComponent>
              </div>

              <div className="col-lg-4 mb-3">
                <CardComponent
                  title="Card Payments"
                  headerColor="primary"
                  pull="left"
                >
                  <div className="payment-breakdown">
                    <div className="payment-amount">
                      <FaCreditCard className="text-primary" />
                      <span className="ms-2 fw-bold">₹{analyticsData.cardAmount.toLocaleString()}</span>
                    </div>
                    <div className="payment-details mt-2">
                      <small className="text-muted">{analyticsData.cardCount} transactions</small>
                      <span className="badge bg-primary">{analyticsData.cardPercentage}% of total</span>
                    </div>
                  </div>
                </CardComponent>
              </div>

              <div className="col-lg-4 mb-3">
                <CardComponent
                  title="Bank Transfers"
                  headerColor="info"
                  pull="left"
                >
                  <div className="payment-breakdown">
                    <div className="payment-amount">
                      <FaUniversity className="text-info" />
                      <span className="ms-2 fw-bold">₹{analyticsData.transferAmount.toLocaleString()}</span>
                    </div>
                    <div className="payment-details mt-2">
                      <small className="text-muted">{analyticsData.transferCount} transactions</small>
                      <span className="badge bg-info">{analyticsData.transferPercentage}% of total</span>
                    </div>
                  </div>
                </CardComponent>
              </div>
            </div>

            {/* Performance Summary */}
            <div className="row mb-4">
              <div className="col-12">
                <CardComponent
                  title="Performance Summary"
                  headerColor="warning"
                  pull="left"
                >
                  <div className="row">
                    <div className="col-md-4 text-center mb-3">
                      <div className="performance-metric">
                        <FaClock className="performance-icon text-warning" />
                        <div className="metric-value">{analyticsData.peakHour}</div>
                        <div className="metric-label">Peak Hour</div>
                      </div>
                    </div>
                    <div className="col-md-4 text-center mb-3">
                      <div className="performance-metric">
                        <FaTrophy className="performance-icon text-warning" />
                        <div className="metric-value">₹{analyticsData.highestTransaction}</div>
                        <div className="metric-label">Highest Transaction</div>
                      </div>
                    </div>
                    <div className="col-md-4 text-center mb-3">
                      <div className="performance-metric">
                        <FaExchangeAlt className="performance-icon text-warning" />
                        <div className="metric-value">{analyticsData.tablesServed}</div>
                        <div className="metric-label">Tables Served</div>
                      </div>
                    </div>
                  </div>
                </CardComponent>
              </div>
            </div>
          </>
        )}

        {/* Quick Actions */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex gap-2 flex-wrap">
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/reports/billhistory')}
              >
                <FaFileAlt /> Full Reports
              </button>
              <button 
                className="btn btn-success"
                onClick={() => navigate('/sale/pos')}
              >
                <FaExchangeAlt /> New Sale
              </button>
            </div>
          </div>
        </div>

        {/* Recent Transactions Table */}
        <div className="row">
          <div className="col-12">
            <CardComponent
              title={`Recent Transactions (${transactions.length} transactions for ${selectedPeriod})`}
              headerColor="secondary"
              pull="left"
            >
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead className="table-dark">
                    <tr>
                      <th>Time</th>
                      <th>Table</th>
                      <th>Payment Mode</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.slice(0, 15).map((transaction) => (
                      <tr key={transaction.id}>
                        <td>{new Date(`1970-01-01T${transaction.tme}Z`).toLocaleTimeString()}</td>
                        <td>Table {transaction.tables}</td>
                        <td>
                          <span className={`badge payment-mode-${transaction.mode}`}>
                            {transaction.mode === 'cash' && <FaDollarSign />}
                            {transaction.mode === 'card' && <FaCreditCard />}
                            {transaction.mode === 'btransfer' && <FaUniversity />}
                            <span className="ms-1">{transaction.mode.toUpperCase()}</span>
                          </span>
                        </td>
                        <td>₹{transaction.netamount.toFixed(2)}</td>
                        <td>
                          <span className={`badge ${transaction.balance > 0 ? 'bg-warning' : 'bg-success'}`}>
                            {transaction.balance > 0 ? 'Pending' : 'Completed'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardComponent>
          </div>
        </div>
      </div>

      {/* Floating Navigation Modal */}
      <FloatingNavModal currentPage="analytics" />
    </div>
  );
};

export default CashierAnalytics;